import React, { useState, useEffect } from "react";
import Layout, { Container } from "../components/layout";
import Pagination from "../components/pagination";
import { apiurl } from "../config/config";

const TicketTable = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalItems, setTotalItems] = useState(0);

  // Filters
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchTickets = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${apiurl}/api/ticket/get`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          page: currentPage,
          limit: itemsPerPage,
          search,
          category: categoryFilter,
          startDate,
          endDate,
        }),
      });

      const data = await res.json();

      if (data?.success) {
        setTickets(data.tickets || []);
        setTotalItems(data.pagination?.totalTickets || 0);
      } else {
        setTickets([]);
        setTotalItems(0);
      }
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setTickets([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [currentPage, itemsPerPage, search, categoryFilter, startDate, endDate]);

  const statusClasses = {
    pending: "bg-yellow-100 text-yellow-700",
    open: "bg-blue-100 text-blue-700",
    resolved: "bg-green-100 text-green-700",
    assigned: "bg-purple-100 text-purple-700",
    hold: "bg-gray-100 text-gray-700",
    cancelled: "bg-red-100 text-red-700",
    closed: "bg-gray-200 text-gray-800",
    completed: "bg-green-200 text-green-800",
  };

  const priorityClasses = {
    low: "bg-gray-100 text-gray-700",
    medium: "bg-blue-100 text-blue-700",
    high: "bg-orange-100 text-orange-700",
    critical: "bg-red-100 text-red-700",
  };

  return (
    <div className="overflow-x-auto bg-white p-4 rounded-2xl shadow-lg">
      {/* Filters */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <input
            type="text"
            placeholder="Search by ID, subject, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded-lg p-2 text-sm w-full md:w-64"
          />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border rounded-lg p-2 text-sm w-full md:w-48"
          >
            <option value="">All Categories</option>
            <option value="Payslip Issue">Payslip Issue</option>
            <option value="Leave Discrepancy">Leave Discrepancy</option>
            <option value="Tax Deduction">Tax Deduction</option>
            <option value="Bonus/Allowance">Bonus/Allowance</option>
            <option value="Others">Others</option>
          </select>
        </div>

        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <label className="flex flex-col text-sm">
            Start Date
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border rounded-lg p-2"
            />
          </label>
          <label className="flex flex-col text-sm">
            End Date
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border rounded-lg p-2"
            />
          </label>
        </div>

        <select
          value={itemsPerPage}
          onChange={(e) => {
            setItemsPerPage(Number(e.target.value));
            setCurrentPage(1);
          }}
          className="border rounded-lg p-2 text-sm w-36"
        >
          {[100, 200, 500, 1000, 2000].map((limit) => (
            <option key={limit} value={limit}>
              {limit} / page
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="text-center text-gray-500 py-6">Loading tickets...</p>
      ) : tickets.length > 0 ? (
        <table className="min-w-full text-left text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="py-2 px-4 border-b">Ticket ID</th>
              <th className="py-2 px-4 border-b">Team</th>
              <th className="py-2 px-4 border-b">Subject</th>
              <th className="py-2 px-4 border-b">Category</th>
              <th className="py-2 px-4 border-b">Status</th>
              <th className="py-2 px-4 border-b">Priority</th>
              <th className="py-2 px-4 border-b">Requester</th>
              <th className="py-2 px-4 border-b">Date Raised</th>
              <th className="py-2 px-4 border-b">Action</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((ticket) => (
              <tr key={ticket._id} className="hover:bg-gray-50 transition">
                <td className="py-2 px-4 border-b">{ticket.id}</td>
                <td className="py-2 px-4 border-b">{ticket.serviceType || "-"}</td>
                <td className="py-2 px-4 border-b">{ticket.subject || "-"}</td>
                <td className="py-2 px-4 border-b">{ticket.category || "-"}</td>
                <td className="py-2 px-4 border-b">
                  <span className={`py-1 px-3 rounded-full text-xs ${statusClasses[ticket.status] || "bg-gray-100 text-gray-700"}`}>
                    {ticket.status}
                  </span>
                </td>
                <td className="py-2 px-4 border-b">
                  <span className={`py-1 px-3 rounded-full text-xs ${priorityClasses[ticket.priority] || "bg-gray-100 text-gray-700"}`}>
                    {ticket.priority || "-"}
                  </span>
                </td>
                <td className="py-2 px-4 border-b">
                  {ticket?.requester?.userName || "-"}
                  <br />
                  <span className="text-gray-500 text-xs">{ticket?.requester?.userEmail || ""}</span>
                </td>
                <td className="py-2 px-4 border-b">
                  {new Date(ticket.createdAt).toLocaleDateString()}
                </td>
                <td className="py-2 px-4 border-b">
                  <button
                    onClick={() => setOpenModal(ticket)}
                    className="text-blue-500 hover:underline text-sm"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-center text-gray-500 py-6">No tickets found.</p>
      )}

      <div className="mt-6">
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>

      {/* Modal */}
      {openModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-semibold mb-4">Ticket Details</h2>
            <div className="space-y-2">
              <p><strong>ID:</strong> {openModal.id}</p>
              <p><strong>Subject:</strong> {openModal.subject || "-"}</p>
              <p><strong>Category:</strong> {openModal.category || "-"}</p>
              <p><strong>Status:</strong> {openModal.status}</p>
              <p><strong>Priority:</strong> {openModal.priority}</p>
              <p><strong>Service Type:</strong> {openModal.serviceType || "-"}</p>
              <p>
                <strong>Requester:</strong>{" "}
                {openModal?.requester?.firstname} {openModal?.requester?.lastname} <br />
                <span className="text-gray-500 text-sm">{openModal?.requester?.email}</span>
              </p>
              {openModal.relatedEmployee && (
                <p>
                  <strong>Related Employee:</strong> {openModal.relatedEmployee.name} ({openModal.relatedEmployee.employeeId})
                </p>
              )}
              {openModal.relatedAsset && (
                <p>
                  <strong>Related Asset:</strong> {openModal.relatedAsset.name} ({openModal.relatedAsset.assetId})
                </p>
              )}
              {openModal.relatedComplaint && (
                <p>
                  <strong>Related Complaint:</strong> {openModal.relatedComplaint.title} ({openModal.relatedComplaint.complaintId})
                </p>
              )}
              <p><strong>Date Raised:</strong> {new Date(openModal.createdAt).toLocaleString()}</p>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setOpenModal(null)}
                className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TicketList = () => (
  <Layout>
    <Container overflow={"overflow-scroll"}>
      <div className="p-2">
        <TicketTable />
      </div>
    </Container>
  </Layout>
);

export default TicketList;